/**
 * Tenant (Water Company) Registration — Multi-Step
 * تسجيل شركة مياه جديدة بخطوات متعددة
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

const SAUDI_CITIES = [
  'الرياض','جدة','مكة المكرمة','المدينة المنورة','الدمام','الخبر',
  'الظهران','الأحساء','الطائف','تبوك','بريدة','القصيم','أبها',
  'خميس مشيط','حائل','نجران','جيزان','ينبع','القطيف','الجبيل',
];

const REGIONS = [
  'الرياض','مكة المكرمة','المدينة المنورة','القصيم','الشرقية',
  'عسير','تبوك','حائل','الحدود الشمالية','جازان','نجران','الباحة','الجوف',
];

const WATER_TYPES = [
  { key: 'drinking',     label: 'مياه شرب',             icon: '💧' },
  { key: 'non_drinking', label: 'مياه غير صالحة للشرب', icon: '🚰' },
  { key: 'treated',      label: 'مياه معالجة',           icon: '♻️' },
];

const STEPS = [
  { id: 1, title: 'معلومات الشركة', icon: '🏢' },
  { id: 2, title: 'المالك والترخيص', icon: '📋' },
  { id: 3, title: 'تأكيد التسجيل', icon: '✅' },
];

export default function TenantRegisterPage() {
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [reqDefs, setReqDefs] = useState([]);

  const [company, setCompany] = useState({
    company_name: '', company_name_en: '',
    city: '', region: '', phone: '',
    water_types: [],
  });
  const [owner, setOwner] = useState({
    owner_name: '', owner_phone: '', email: '',
    password: '', confirm_password: '',
  });
  const [reqData, setReqData] = useState({});

  useEffect(() => {
    fetch(`${API}/tenant/auth/register/requirements`)
      .then(r => r.json())
      .then(d => setReqDefs(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const setC = (k, v) => setCompany(p => ({ ...p, [k]: v }));
  const setO = (k, v) => setOwner(p => ({ ...p, [k]: v }));
  const setR = (k, v) => setReqData(p => ({ ...p, [k]: v }));

  const toggleWater = (key) => {
    setCompany(p => ({
      ...p,
      water_types: p.water_types.includes(key)
        ? p.water_types.filter(k => k !== key)
        : [...p.water_types, key],
    }));
  };

  /* validations */
  const validateStep1 = () => {
    if (!company.company_name.trim()) return 'اسم الشركة مطلوب';
    if (!company.phone.trim()) return 'رقم الجوال مطلوب';
    if (!/^(05|5)\d{8}$/.test(company.phone.trim())) return 'رقم الجوال غير صحيح';
    if (!company.city) return 'المدينة مطلوبة';
    return null;
  };

  const validateStep2 = () => {
    if (!owner.owner_name.trim()) return 'اسم المالك / المدير مطلوب';
    if (!owner.email.trim()) return 'البريد الإلكتروني مطلوب';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email)) return 'البريد الإلكتروني غير صحيح';
    if (owner.password.length < 6) return 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    if (owner.password !== owner.confirm_password) return 'كلمة المرور غير متطابقة';
    return null;
  };

  const nextStep = () => {
    setError('');
    const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
    if (err) { setError(err); return; }
    setStep(s => s + 1);
  };

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/tenant/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name:      company.company_name.trim(),
          company_name_en:   company.company_name_en.trim() || null,
          city:              company.city,
          region:            company.region || null,
          phone:             company.phone.trim(),
          email:             owner.email.trim().toLowerCase(),
          password:          owner.password,
          owner_name:        owner.owner_name.trim(),
          owner_phone:       owner.owner_phone.trim() || null,
          requirements_data: { water_types: company.water_types, ...reqData },
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'خطأ في التسجيل'); return; }
      setSuccess(data.message || 'تم استلام طلبك');
    } catch { setError('خطأ في الاتصال'); }
    finally   { setLoading(false); }
  };

  /* Success */
  if (success) return (
    <div style={styles.page} dir="rtl">
      <div style={{...styles.card, textAlign:'center', padding:'48px 28px'}}>
        <div style={{fontSize:72, marginBottom:16}}>🎉</div>
        <h2 style={{fontSize:22, fontWeight:900, color:'#1e293b', marginBottom:8}}>
          تم استلام طلب التسجيل!
        </h2>
        <p style={{color:'#64748b', lineHeight:1.7, marginBottom:8}}>{success}</p>
        <p style={{color:'#94a3b8', fontSize:13, marginBottom:32}}>
          سيقوم فريق Watrly بمراجعة طلبكم والتواصل معكم خلال 2-3 أيام عمل
        </p>
        <Link to="/tenant/login" style={styles.btn}>
          العودة لتسجيل الدخول
        </Link>
      </div>
    </div>
  );

  /* Step bar */
  const StepBar = () => (
    <div style={{display:'flex', alignItems:'center', marginBottom:28}}>
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', flex:1}}>
            <div style={{
              width:36, height:36, borderRadius:'50%',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:15, fontWeight:'bold',
              background: step > s.id ? '#22c55e' : step === s.id ? '#3b82f6' : '#e2e8f0',
              color: step >= s.id ? 'white' : '#94a3b8',
              marginBottom:4, transition:'all 0.2s',
            }}>
              {step > s.id ? '✓' : s.icon}
            </div>
            <span style={{fontSize:11, color: step===s.id ? '#3b82f6' : '#94a3b8', textAlign:'center', fontWeight: step===s.id?'bold':'normal'}}>
              {s.title}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{height:2, flex:1, background: step > s.id+1 ? '#22c55e' : '#e2e8f0', marginBottom:20}}/>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  /* Requirement field renderer */
  const renderReqField = (req) => {
    const val = reqData[req.field_key] || '';
    const inp = {
      style: {
        width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f0',
        borderRadius:10, fontSize:14, outline:'none', background:'white',
        boxSizing:'border-box', fontFamily:'inherit',
      },
      placeholder: req.placeholder_ar || '',
    };
    if (req.field_type === 'select' && req.options) {
      const opts = typeof req.options === 'string' ? JSON.parse(req.options) : req.options;
      return (
        <select {...inp} value={val} onChange={e => setR(req.field_key, e.target.value)}>
          <option value="">— اختر —</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label_ar || o.label_en}</option>)}
        </select>
      );
    }
    if (req.field_type === 'date') {
      return <input type="date" {...inp} value={val} onChange={e => setR(req.field_key, e.target.value)}/>;
    }
    if (req.field_type === 'file') {
      return (
        <div>
          <input type="file"
            accept={(req.file_types||'image/*').split(',').map(t=>`.${t.trim()}`).join(',')}
            onChange={e => setR(req.field_key, e.target.files[0]?.name || '')}
            style={{fontSize:13, padding:'8px 0'}}/>
          <small style={{fontSize:11,color:'#94a3b8',display:'block',marginTop:2}}>
            {req.file_types||'JPG، PNG، PDF'}{req.max_file_size ? ` | الحجم الأقصى ${(req.max_file_size/1024).toFixed(0)} MB` : ''}
          </small>
        </div>
      );
    }
    return <input {...inp} dir="ltr" value={val} onChange={e => setR(req.field_key, e.target.value)}/>;
  };

  /* Group reqs by group_key */
  const groupedReqs = reqDefs.reduce((acc, r) => {
    const g = r.group_key || 'other';
    if (!acc[g]) acc[g] = { name: r.group_name_ar, items: [] };
    acc[g].items.push(r);
    return acc;
  }, {});

  const F = ({ label, required, children }) => (
    <div style={{marginBottom:16}}>
      <label style={{display:'block', fontSize:13, fontWeight:'bold', color:'#374151', marginBottom:6}}>
        {label}{required && <span style={{color:'#ef4444', marginRight:3}}>*</span>}
      </label>
      {children}
    </div>
  );

  const Input = ({ ...props }) => (
    <input style={{
      width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f0',
      borderRadius:10, fontSize:14, outline:'none', background:'white',
      boxSizing:'border-box', fontFamily:'inherit',
      transition:'border-color 0.15s',
    }} {...props}/>
  );

  const Select = ({ options, ...props }) => (
    <select style={{
      width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f0',
      borderRadius:10, fontSize:14, outline:'none', background:'white',
      boxSizing:'border-box', fontFamily:'inherit',
    }} {...props}>
      <option value="">— اختر —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div style={styles.page} dir="rtl">
      {/* Logo */}
      <div style={styles.logo}>
        <div style={{width:44,height:44,background:'#3b82f6',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg viewBox="0 0 32 32" fill="none" style={{width:28,height:28}}>
            <path d="M16 4C10 10 7 14 7 19a9 9 0 0 0 18 0c0-5-3-9-9-15Z" fill="white"/>
          </svg>
        </div>
        <div>
          <div style={{fontWeight:900,fontSize:18,color:'#1e293b'}}>Watrly</div>
          <div style={{fontSize:12,color:'#64748b'}}>تسجيل شركة مياه جديدة</div>
        </div>
      </div>

      <div style={styles.card}>
        <StepBar/>

        {/* ─── Step 1: Company Info ─── */}
        {step === 1 && (
          <div>
            <h3 style={styles.stepTitle}>🏢 معلومات الشركة</h3>
            <p style={styles.stepSub}>أدخل المعلومات الأساسية لشركتك</p>

            <F label="اسم الشركة (عربي)" required>
              <Input value={company.company_name} placeholder="شركة مياه الرياض"
                onChange={e => setC('company_name', e.target.value)}/>
            </F>
            <F label="اسم الشركة (إنجليزي)">
              <Input dir="ltr" value={company.company_name_en} placeholder="Riyadh Water Company"
                onChange={e => setC('company_name_en', e.target.value)}/>
            </F>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
              <F label="المدينة" required>
                <Select options={SAUDI_CITIES} value={company.city}
                  onChange={e => setC('city', e.target.value)}/>
              </F>
              <F label="المنطقة">
                <Select options={REGIONS} value={company.region}
                  onChange={e => setC('region', e.target.value)}/>
              </F>
            </div>
            <F label="رقم جوال الشركة" required>
              <Input dir="ltr" value={company.phone} placeholder="05XXXXXXXX"
                onChange={e => setC('phone', e.target.value)}/>
            </F>

            {/* Water types */}
            <F label="أنواع المياه التي تعمل بها">
              <div style={{display:'flex', flexDirection:'column', gap:8}}>
                {WATER_TYPES.map(wt => (
                  <button key={wt.key} type="button"
                    onClick={() => toggleWater(wt.key)}
                    style={{
                      display:'flex', alignItems:'center', gap:12,
                      padding:'12px 14px', borderRadius:12,
                      border: company.water_types.includes(wt.key)
                        ? '2px solid #3b82f6' : '1.5px solid #e2e8f0',
                      background: company.water_types.includes(wt.key) ? '#eff6ff' : 'white',
                      cursor:'pointer', textAlign:'right', transition:'all 0.15s',
                    }}>
                    <span style={{fontSize:22}}>{wt.icon}</span>
                    <span style={{fontWeight:'bold', fontSize:14,
                      color: company.water_types.includes(wt.key) ? '#1d4ed8' : '#374151'}}>
                      {wt.label}
                    </span>
                    <div style={{
                      marginRight:'auto', width:20, height:20, borderRadius:'50%',
                      border: company.water_types.includes(wt.key) ? '2px solid #3b82f6' : '2px solid #cbd5e1',
                      background: company.water_types.includes(wt.key) ? '#3b82f6' : 'white',
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                      {company.water_types.includes(wt.key) &&
                        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" style={{width:12}}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      }
                    </div>
                  </button>
                ))}
              </div>
            </F>
          </div>
        )}

        {/* ─── Step 2: Owner + Requirements ─── */}
        {step === 2 && (
          <div>
            <h3 style={styles.stepTitle}>📋 المالك والترخيص</h3>
            <p style={styles.stepSub}>بيانات المالك أو المدير المسؤول عن الحساب</p>

            <F label="اسم المالك / المدير العام" required>
              <Input value={owner.owner_name} placeholder="أحمد محمد العلي"
                onChange={e => setO('owner_name', e.target.value)}/>
            </F>
            <F label="جوال المالك">
              <Input dir="ltr" value={owner.owner_phone} placeholder="05XXXXXXXX"
                onChange={e => setO('owner_phone', e.target.value)}/>
            </F>
            <F label="البريد الإلكتروني (للدخول)" required>
              <Input dir="ltr" type="email" value={owner.email} placeholder="info@company.com"
                onChange={e => setO('email', e.target.value)}/>
            </F>
            <F label="كلمة المرور" required>
              <Input type="password" value={owner.password} placeholder="6 أحرف على الأقل"
                onChange={e => setO('password', e.target.value)}/>
            </F>
            <F label="تأكيد كلمة المرور" required>
              <Input type="password" value={owner.confirm_password} placeholder="أعد كتابة كلمة المرور"
                onChange={e => setO('confirm_password', e.target.value)}/>
            </F>

            {/* Dynamic requirements from DB */}
            {Object.keys(groupedReqs).length > 0 && (
              <div style={{marginTop:24}}>
                <p style={{fontSize:13, fontWeight:'bold', color:'#64748b', marginBottom:16,
                  borderTop:'1px solid #f1f5f9', paddingTop:16}}>
                  📄 الوثائق التجارية (يمكن رفعها لاحقاً)
                </p>
                {Object.entries(groupedReqs).slice(0, 2).map(([gk, g]) => (
                  <div key={gk} style={{marginBottom:20}}>
                    <div style={{fontSize:11, fontWeight:'bold', color:'#94a3b8',
                      textTransform:'uppercase', letterSpacing:'0.05em',
                      marginBottom:10, paddingBottom:6, borderBottom:'1px solid #f8fafc'}}>
                      {g.name}
                    </div>
                    {g.items.slice(0, 3).map(req => (
                      <div key={req.field_key} style={{marginBottom:14}}>
                        <label style={{display:'block', fontSize:13, fontWeight:'bold',
                          color:'#374151', marginBottom:5}}>
                          {req.field_name_ar}
                          {req.is_required && <span style={{color:'#ef4444', marginRight:3}}>*</span>}
                        </label>
                        {renderReqField(req)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 3: Confirm ─── */}
        {step === 3 && (
          <div>
            <h3 style={styles.stepTitle}>✅ مراجعة وتأكيد</h3>
            <p style={styles.stepSub}>راجع البيانات قبل إرسال الطلب</p>

            <div style={{background:'#f8fafc', borderRadius:14, padding:16, marginBottom:14}}>
              <p style={{fontSize:12, fontWeight:'bold', color:'#64748b', marginBottom:10}}>الشركة</p>
              {[
                ['الاسم', company.company_name],
                ['المدينة', company.city],
                ['الجوال', company.phone],
                ['أنواع المياه', company.water_types.map(w => WATER_TYPES.find(x=>x.key===w)?.label).join('، ') || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{display:'flex', justifyContent:'space-between',
                  marginBottom:6, fontSize:14}}>
                  <span style={{color:'#94a3b8'}}>{k}</span>
                  <span style={{fontWeight:'bold', color:'#1e293b', textAlign:'left', maxWidth:'60%'}}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{background:'#f8fafc', borderRadius:14, padding:16, marginBottom:14}}>
              <p style={{fontSize:12, fontWeight:'bold', color:'#64748b', marginBottom:10}}>المالك</p>
              {[
                ['الاسم', owner.owner_name],
                ['البريد', owner.email],
              ].map(([k, v]) => (
                <div key={k} style={{display:'flex', justifyContent:'space-between',
                  marginBottom:6, fontSize:14}}>
                  <span style={{color:'#94a3b8'}}>{k}</span>
                  <span style={{fontWeight:'bold', color:'#1e293b'}} dir="ltr">{v}</span>
                </div>
              ))}
            </div>

            <div style={{
              background:'#eff6ff', borderRadius:12, padding:14,
              border:'1px solid #bfdbfe', marginBottom:20,
            }}>
              <p style={{fontSize:13, color:'#1e40af', margin:0, lineHeight:1.7}}>
                📋 بعد التسجيل سيتم مراجعة طلبكم من فريق Watrly.
                سيتم تفعيل الحساب وإشعاركم عند الموافقة خلال 2-3 أيام عمل.
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background:'#fef2f2', border:'1px solid #fecaca',
            borderRadius:10, padding:'10px 14px', marginBottom:14,
            color:'#dc2626', fontSize:13,
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{display:'flex', gap:10, marginTop:8}}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} style={styles.btnSecondary}>
              ← السابق
            </button>
          )}
          {step < 3 ? (
            <button onClick={nextStep} style={{...styles.btn, flex:2}}>
              التالي →
            </button>
          ) : (
            <button onClick={submit} disabled={loading} style={{...styles.btn, flex:2, opacity:loading?0.7:1}}>
              {loading
                ? <span style={{display:'inline-block',width:18,height:18,border:'2px solid rgba(255,255,255,0.3)',
                    borderTop:'2px solid white',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
                : 'إرسال الطلب 🚀'}
            </button>
          )}
        </div>

        <p style={{textAlign:'center', marginTop:16, fontSize:13, color:'#64748b'}}>
          لديك حساب؟{' '}
          <Link to="/tenant/login" style={{color:'#3b82f6', fontWeight:'bold', textDecoration:'none'}}>
            سجّل دخول
          </Link>
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight:'100vh', background:'linear-gradient(135deg,#eff6ff 0%,#f0fdf4 100%)',
    display:'flex', flexDirection:'column', alignItems:'center',
    justifyContent:'flex-start', padding:'32px 16px 60px',
  },
  logo: {
    display:'flex', alignItems:'center', gap:12, marginBottom:24,
  },
  card: {
    background:'white', borderRadius:20, padding:'28px 24px',
    width:'100%', maxWidth:520, boxShadow:'0 4px 32px rgba(0,0,0,0.08)',
  },
  stepTitle: {
    margin:'0 0 4px', fontSize:20, fontWeight:900, color:'#1e293b',
  },
  stepSub: {
    color:'#64748b', fontSize:13, marginBottom:22,
  },
  btn: {
    flex:1, padding:'13px 20px', background:'#3b82f6', color:'white',
    border:'none', borderRadius:12, fontSize:15, fontWeight:'bold',
    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
    gap:6, transition:'background 0.15s', textDecoration:'none',
  },
  btnSecondary: {
    flex:1, padding:'13px 20px', background:'#f1f5f9', color:'#475569',
    border:'none', borderRadius:12, fontSize:14, fontWeight:'bold',
    cursor:'pointer',
  },
};
